import {computeAreaPoint, computePoints, constructPolygonsWith2Areas} from "./UtilityFunctions";

function computeVoltaSymmetricalShed(instalationInfo, totalModules) {
    /*
                    60m / 51m
      p1---------------------------------------p4
      |  pn1------------------------------pn4  |
      |  |                                 |   |
      |  |                                 |   |
      |  pn2------------------------------pn3  |
      p6---------------------------------------p5  26m
      |  ps1------------------------------ps4  |
      |  |                                 |   |
      |  |                                 |   |
      |  ps2------------------------------ps3  |
      p2---------------------------------------p3

    */
    // google variable api
    const google = window.google;
    const margeBetweenModules = instalationInfo.margeBetweenModules;
    const inclination = instalationInfo.inclinaison;
    const margeBetweenSide = 0.1;
    const margeMiddleRidge = 0.5;
    const preserveMiddleMargin = instalationInfo.implantationType === "[Volta] Hangar Symétrique 45m/17.6m" || instalationInfo.implantationType === "[Volta] Hangar Symétrique Manège 60m/25m" || instalationInfo.implantationType === "[Volta] Hangar Symétrique Manège 60m/30m";

    const p1 = new google.maps.LatLng(instalationInfo.coordinates[0]);
    const p2 = new google.maps.LatLng(instalationInfo.coordinates[1]);
    const p3 = new google.maps.LatLng(instalationInfo.coordinates[2]);
    const p4 = new google.maps.LatLng(instalationInfo.coordinates[3]);

    // compute 4 different orientations
    const orientation12 = google.maps.geometry.spherical.computeHeading(p1, p2);
    const orientation21 = google.maps.geometry.spherical.computeHeading(p2, p1);
    const orientation23 = google.maps.geometry.spherical.computeHeading(p2, p3);
    const orientation32 = google.maps.geometry.spherical.computeHeading(p3, p2);

    // compute the middle point (p5 is not necessary because we don't need to compute pn3 or the other side)
    const p6 = new google.maps.LatLng(google.maps.geometry.spherical.computeOffset(p1, google.maps.geometry.spherical.computeDistanceBetween(p1, p2) / 2, orientation12));

    // compute the first solar panel area (north)
    let pn1 = computeAreaPoint(p1, margeBetweenSide, margeBetweenSide, orientation12, orientation23);
    let pn2 = computeAreaPoint(p6, margeMiddleRidge, margeBetweenSide, orientation21, orientation23);
    let pn4 = computeAreaPoint(p4, margeBetweenSide, margeBetweenSide, orientation21, orientation32);

    // size of the area (north)
    const sizeOfAreaNorth = {
        width: google.maps.geometry.spherical.computeDistanceBetween(pn1, pn2),
        length: google.maps.geometry.spherical.computeDistanceBetween(pn1, pn4)
    };

    // compute the second solar panel area (south)
    let ps1 = computeAreaPoint(p6, margeMiddleRidge, margeBetweenSide, orientation12, orientation23);
    let ps2 = computeAreaPoint(p2, margeBetweenSide, margeBetweenSide, orientation21, orientation23);
    let ps3 = computeAreaPoint(p3, margeBetweenSide, margeBetweenSide, orientation12, orientation32);

    // size of the area (south)
    const sizeOfAreaSouth = {
        width: google.maps.geometry.spherical.computeDistanceBetween(ps1, ps2),
        length: google.maps.geometry.spherical.computeDistanceBetween(ps2, ps3)
    };

    // size of one module
    const sizeOfModuleBase = {
        length: instalationInfo.height + margeBetweenModules,
        width: instalationInfo.width + margeBetweenModules
    };

    // compute number of modules that fit in one pan
    const modulesPerPan = totalModules / 2;

    function findBestGrid(area, moduleSize, inclination, targetModules) {
        const correctedWidth = moduleSize.width * Math.cos(inclination * Math.PI / 180);
        const maxCols = Math.ceil(area.length / moduleSize.length) + 2; // +2 pour autoriser un léger dépassement
        const maxRows = Math.ceil(area.width / correctedWidth) + 2;

        let bestCols = 0, bestRows = 0;
        let bestDelta = Infinity;

        for (let cols = 1; cols <= maxCols; cols++) {
            for (let rows = 1; rows <= maxRows; rows++) {
                const placedModules = cols * rows;
                const delta = Math.abs(placedModules - targetModules);

                const totalLength = cols * moduleSize.length;
                const totalWidth = rows * correctedWidth;

                // on autorise jusqu'à 10% de dépassement de la surface
                const lengthOverflow = totalLength / area.length;
                const widthOverflow = totalWidth / area.width;

                if (lengthOverflow <= 1.1 && widthOverflow <= 1.1) {
                    if (delta < bestDelta) {
                        bestDelta = delta;
                        bestCols = cols;
                        bestRows = rows;
                    }
                }
            }
        }

        return { cols: bestCols, rows: bestRows };
    }

    // find the best grid configuration for both areas
    const gridNorth = findBestGrid(sizeOfAreaNorth, sizeOfModuleBase, inclination, modulesPerPan);
    const gridSouth = findBestGrid(sizeOfAreaSouth, sizeOfModuleBase, inclination, modulesPerPan);

    const middleMarginNorth = instalationInfo.preserveMiddleMargin ? margeMiddleRidge : 0;
    const middleMarginSouth = instalationInfo.preserveMiddleMargin ? margeMiddleRidge : 0;

    // compute the new starting point (north)
    pn1 = computeAreaPoint(pn1,
        (sizeOfAreaNorth.width - gridNorth.rows * sizeOfModuleBase.width * Math.cos(inclination * Math.PI / 180)) / 2 + middleMarginNorth,
        (sizeOfAreaNorth.length - gridNorth.cols * sizeOfModuleBase.length) / 2,
        orientation12, orientation23
    );

    // compute the new starting point (south)
    ps1 = computeAreaPoint(ps1,
        (sizeOfAreaSouth.width - gridSouth.rows * sizeOfModuleBase.width * Math.cos(inclination * Math.PI / 180)) / 2 + middleMarginSouth,
        (sizeOfAreaSouth.length - gridSouth.cols * sizeOfModuleBase.length) / 2,
        orientation12, orientation23
    );

    // compute the coords of all the panels
    const northPanelCoords = constructPolygonsWith2Areas(
        computePoints({col: gridNorth.cols, row: gridNorth.rows}, sizeOfModuleBase, pn1, {first: orientation23, second: orientation12})
    );
    const southPanelCoords = constructPolygonsWith2Areas(
        computePoints({col: gridSouth.cols, row: gridSouth.rows}, sizeOfModuleBase, ps1, {first: orientation23, second: orientation12})
    );

    console.warn(`Total modules demandé: ${totalModules}, modules placés: ${gridNorth.cols * gridNorth.rows + gridSouth.cols * gridSouth.rows}`);

    return [...southPanelCoords,  ...northPanelCoords];
}
export default computeVoltaSymmetricalShed;
